import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  stripSearchParams,
} from '@tanstack/react-router';
import { RootLayout } from '@/components/layouts';
import MatterAccessGuard from '@/components/shared/MatterAccessGuard';
import { MicrosoftAdminConsentResult } from '@/components/shared/MicrosoftAdminConsentResult';
import { NotFoundFallback } from '@/components/shared/NotFoundFallback';
import { RouteErrorFallback } from '@/components/shared/RouteErrorFallback';
import { createLogger } from '@/lib/logger';
import AdminPage from '@/pages/admin';
import LoginPage from '@/pages/auth/login';
import AllMattersPage from '@/pages/all-matters';
import IManageAuthCallbackPage from '@/pages/imanage-auth-callback';
import BundleDetailPage, { BinderDetailPage } from '@/pages/matter/[slug]/bundles/[bundleId]';
import BundleEditPage, { BinderEditPage } from '@/pages/matter/[slug]/bundles/[bundleId]/edit';
import BundlesIndexPage, { BindersIndexPage } from '@/pages/matter/[slug]/bundles/index';
import AllFilesPage from '@/pages/matter/[slug]/case-files/all-files';
import CorrespondencePage from '@/pages/matter/[slug]/case-files/correspondence';
import FolderPage, { CorrespondenceFolderPage } from '@/pages/matter/[slug]/case-files/folder/[folderGuid]';
import CaseFilesIndexPage from '@/pages/matter/[slug]/case-files/index';
import CasePartnerPage from '@/pages/matter/[slug]/case-partner';
import ChronologyPage from '@/pages/matter/[slug]/chronology';
import DeadlinesPage from '@/pages/matter/[slug]/deadlines';
import DocketsPage from '@/pages/matter/[slug]/dockets';
import DraftDetailPage from '@/drafting/pages/DraftDetailPage';
import DraftingIndexPage from '@/pages/matter/[slug]/drafts/index';
import EntitiesPage from '@/pages/matter/[slug]/entities';
import EntityAdminPage from '@/pages/matter/[slug]/entity-admin';
import FileViewerPage from '@/pages/matter/[slug]/f/[fileId]';
import MembersPage from '@/pages/matter/[slug]/members';
import OverviewPage from '@/pages/matter/[slug]/overview';
import SettingsPage from '@/pages/matter/[slug]/settings';
import SyncPage from '@/pages/matter/[slug]/sync';
import UploadsPage from '@/pages/matter/[slug]/uploads';
import OneDriveAuthCallbackPage from '@/pages/onedrive-auth-callback';
import AccountSettingsPage from '@/pages/settings/account';
import {
  adminConsentSearchSchema,
  adminSearchSchema,
  caseFilesSearchSchema,
  casePartnerSearchSchema,
  deadlinesSearchSchema,
  docketsSearchSchema,
  draftDetailSearchSchema,
  fileViewerSearchSchema,
  folderSearchSchema,
  homeSearchSchema,
  isAdminTab,
  loginSearchSchema,
  oauthCallbackSearchSchema,
  overviewSearchSchema,
  platformSearchSchema,
} from './search-schemas';

const rootRoute = createRootRoute({
  component: RootLayout,
});

// `staticData: { noAuth: true }` tells RootLayout to render <Outlet/>
// directly instead of wrapping in <AccountLayout>.

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/auth/login' });
  },
  staticData: { noAuth: true },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/login',
  component: LoginPage,
  validateSearch: loginSearchSchema,
  staticData: { noAuth: true },
});

const imanageAuthCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/imanage-auth-callback',
  component: IManageAuthCallbackPage,
  validateSearch: oauthCallbackSearchSchema,
  staticData: { noAuth: true },
});

const onedriveAuthCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onedrive-auth-callback',
  component: OneDriveAuthCallbackPage,
  validateSearch: oauthCallbackSearchSchema,
  staticData: { noAuth: true },
});

const onedriveAdminConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onedrive-admin-consent',
  component: () => (
    <MicrosoftAdminConsentResult provider="onedrive" from="/onedrive-admin-consent" />
  ),
  validateSearch: adminConsentSearchSchema,
  staticData: { noAuth: true },
});

const sharepointAdminConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sharepoint-admin-consent',
  component: () => (
    <MicrosoftAdminConsentResult provider="sharepoint" from="/sharepoint-admin-consent" />
  ),
  validateSearch: adminConsentSearchSchema,
  staticData: { noAuth: true },
});

// ─── Authenticated routes (wrapped by <AccountLayout>) ─────────────────────

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/home',
  component: lazyRouteComponent(() => import('@/pages/home')),
});

const allMattersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/all-matters',
  component: AllMattersPage,
  validateSearch: homeSearchSchema,
  search: {
    middlewares: [stripSearchParams({ archived: false, viewAll: false, tab: 'matters' })],
  },
});

const newestHomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/newest-home',
  beforeLoad: () => {
    throw redirect({ to: '/home' });
  },
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: AdminPage,
  validateSearch: adminSearchSchema,
  beforeLoad: ({ location }) => {
    const tab = new URLSearchParams(location.searchStr).get('tab');
    // Legacy bookmarks: Platform used to live under /admin?tab=tenants
    // (and token-usage was a sibling tab on the same page).
    if (tab === 'tenants' || tab === 'token-usage') {
      throw redirect({
        to: '/admin/tenants',
        search: tab === 'token-usage' ? { tab: 'token-usage' } : {},
      });
    }
    // Reject unknown tabs (never shipped) — do not map them onto Analytics
    if (tab != null && tab !== '' && !isAdminTab(tab)) {
      throw redirect({ to: '/admin' });
    }
  },
});
const adminTenantsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/tenants',
  component: lazyRouteComponent(() => import('@/pages/admin/platform')),
  validateSearch: platformSearchSchema,
});

const analyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/analytics',
  // Redirect: Analytics now lives under the firm Admin hub.
  beforeLoad: () => {
    throw redirect({ to: '/admin', search: { tab: 'analytics' } });
  },
});

const accountSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/account',
  component: AccountSettingsPage,
});

// ─── Matter group ($slug provides shared param context) ───────────────────────

const matterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/matter/$slug',
  component: MatterAccessGuard,
});

const overviewRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'overview',
  component: OverviewPage,
  validateSearch: overviewSearchSchema,
});

const matterSettingsRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'settings',
  component: SettingsPage,
});

const syncRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'sync',
  component: SyncPage,
});

const uploadsRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'uploads',
  component: UploadsPage,
});

const membersRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'members',
  component: MembersPage,
});

const deadlinesRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'deadlines',
  component: DeadlinesPage,
  validateSearch: deadlinesSearchSchema,
});

const docketsRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'dockets',
  component: DocketsPage,
  validateSearch: docketsSearchSchema,
});

const entitiesRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'entities',
  component: EntitiesPage,
});

const entityAdminRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'entity-admin',
  component: EntityAdminPage,
});

const chronologyRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'chronology',
  component: ChronologyPage,
});

const casePartnerRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'case-partner',
  component: CasePartnerPage,
  validateSearch: casePartnerSearchSchema,
  remountDeps: ({ params }) => params.slug,
});

const casePartnerIndexRoute = createRoute({
  getParentRoute: () => casePartnerRoute,
  path: '/',
});

const casePartnerChatRoute = createRoute({
  getParentRoute: () => casePartnerRoute,
  path: '$chatGuid',
  validateSearch: casePartnerSearchSchema,
});

const draftingIndexRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'drafting',
  component: DraftingIndexPage,
});

const draftingDetailRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'drafting/$documentGuid',
  component: DraftDetailPage,
  validateSearch: draftDetailSearchSchema,
});

const fileViewerRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'f/$fileId',
  component: FileViewerPage,
  validateSearch: fileViewerSearchSchema,
});

const caseFilesIndexRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'case-files',
  component: CaseFilesIndexPage,
  validateSearch: caseFilesSearchSchema,
});

const allFilesRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'case-files/all-files',
  component: AllFilesPage,
  validateSearch: caseFilesSearchSchema,
});

const folderRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'case-files/folder/$folderGuid',
  component: FolderPage,
  validateSearch: folderSearchSchema,
});

const correspondenceRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'case-files/correspondence',
  component: CorrespondencePage,
  validateSearch: caseFilesSearchSchema,
});

const correspondenceFolderRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'case-files/correspondence/folder/$folderGuid',
  component: CorrespondenceFolderPage,
  validateSearch: folderSearchSchema,
});

const bundlesIndexRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'bundles',
  component: BundlesIndexPage,
});

const bundleDetailRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'bundles/$bundleId',
  component: BundleDetailPage,
});

const bundleEditRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'bundles/$bundleId/edit',
  component: BundleEditPage,
});

// Americanization: `binders` is the US-facing alias for `bundles`. These routes
// render the exact same pages so both /bundles and /binders (and their sub-paths)
// resolve identically. Each alias has a dedicated page wrapper that reads params
// with a concrete, typed `from`, so the shared page bodies stay fully type-safe.
const bindersIndexRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'binders',
  component: BindersIndexPage,
});

const binderDetailRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'binders/$bundleId',
  component: BinderDetailPage,
});

const binderEditRoute = createRoute({
  getParentRoute: () => matterRoute,
  path: 'binders/$bundleId/edit',
  component: BinderEditPage,
});

// ─── Tree assembly ────────────────────────────────────────────────────────────

const routeTree = rootRoute.addChildren([
  // Public
  indexRoute,
  loginRoute,
  imanageAuthCallbackRoute,
  onedriveAuthCallbackRoute,
  onedriveAdminConsentRoute,
  sharepointAdminConsentRoute,
  // Authenticated
  homeRoute,
  allMattersRoute,
  newestHomeRoute,
  adminRoute,
  adminTenantsRoute,
  analyticsRoute,
  accountSettingsRoute,
  matterRoute.addChildren([
    overviewRoute,
    matterSettingsRoute,
    syncRoute,
    uploadsRoute,
    membersRoute,
    deadlinesRoute,
    docketsRoute,
    entitiesRoute,
    entityAdminRoute,
    chronologyRoute,
    casePartnerRoute.addChildren([
      casePartnerIndexRoute,
      casePartnerChatRoute,
    ]),
    draftingIndexRoute,
    draftingDetailRoute,
    fileViewerRoute,
    caseFilesIndexRoute,
    allFilesRoute,
    folderRoute,
    correspondenceRoute,
    correspondenceFolderRoute,
    bundlesIndexRoute,
    bundleDetailRoute,
    bundleEditRoute,
    bindersIndexRoute,
    binderDetailRoute,
    binderEditRoute,
  ]),
]);

const routerLogger = createLogger('RouterError');

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
  scrollToTopSelectors: ['[data-scroll-restoration-id="main-scrollable-area"]'],
  // Branded fallback instead of the library default ("Something went wrong!" with
  // a raw stack). Caught at each route boundary, so page errors render inside the
  // shell while only the failing route is replaced.
  defaultErrorComponent: RouteErrorFallback,
  defaultNotFoundComponent: NotFoundFallback,
  defaultOnCatch: (error, errorInfo) => {
    routerLogger.error('Unhandled error in route tree', error, {
      componentStack: errorInfo.componentStack,
      routerBoundary: true,
    });
  },
});

declare module '@tanstack/history' {
  interface HistoryState {
    /** Set when the side panel expands into the full Case Partner page. */
    hideCasePartnerHistory?: boolean;
  }
}

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }

  // Per-route opt-out for the auth-chrome wrapping done by RootLayout.
  interface StaticDataRouteOption {
    noAuth?: boolean;
  }
}
